import { z } from "zod";
import { CommitShaSchema, IdSchema, SymbolRefSchema, ContractRefSchema, TimestampSchema } from "./primitives.ts";

/**
 * A push to an Artifacts repo. Mirrors the payload of the Artifacts `cf.artifacts.repo.pushed` event.
 * Only the fields Botallion relies on are listed; any extra fields on the real event are kept as-is.
 */
export const PushEventSchema = z.looseObject({
  /** Artifacts namespace the repo lives in. */
  namespace: z.string().min(1),
  /** Repo name within the namespace, for example a session fork. */
  repoName: z.string().min(1),
  /** Full ref that moved, for example `refs/heads/main`. */
  ref: z.string().min(1),
  /** Commit the ref pointed at before the push. All zeros when the ref was created. */
  before: CommitShaSchema,
  /** Commit the ref points at after the push. All zeros when the ref was deleted. */
  after: CommitShaSchema,
});
/** See {@link PushEventSchema}. */
export type PushEvent = z.infer<typeof PushEventSchema>;

/** The `type` string of the Artifacts event {@link PushEventSchema} mirrors. */
export const ARTIFACTS_PUSH_EVENT_TYPE = "cf.artifacts.repo.pushed";

/** The branch name of a `refs/heads/<branch>` ref, or null for tags and other refs. */
export function branchFromRef(ref: string): string | null {
  return ref.startsWith("refs/heads/") ? ref.slice("refs/heads/".length) : null;
}

/**
 * One changed region, in unified-diff terms. `old*` refers to the base commit, `new*` to the pushed commit.
 * A pure insertion has `oldLines: 0`; a pure deletion has `newLines: 0`.
 */
export const HunkSchema = z.object({
  /** First line of the region in the base file (1-based; 0 for a new file). */
  oldStart: z.number().int().min(0),
  /** Number of base lines replaced. */
  oldLines: z.number().int().min(0),
  /** First line of the region in the new file (1-based; 0 for a deleted file). */
  newStart: z.number().int().min(0),
  /** Number of new lines. */
  newLines: z.number().int().min(0),
});
/** See {@link HunkSchema}. */
export type Hunk = z.infer<typeof HunkSchema>;

/** How a file changed. */
export const FileChangeSchema = z.enum(["added", "modified", "deleted", "renamed"]);
/** See {@link FileChangeSchema}. */
export type FileChange = z.infer<typeof FileChangeSchema>;

/** One changed file in a {@link DiffSummary}. */
export const DiffFileSchema = z
  .object({
    /** Repo-relative path after the change. */
    path: z.string().min(1),
    /** How it changed. */
    change: FileChangeSchema,
    /** The path before a rename. Required when `change` is `renamed`. */
    previousPath: z.string().min(1).optional(),
    /** Changed regions. May be empty for binary files. */
    hunks: z.array(HunkSchema),
    /** Symbols touched, where cheap to detect (exported functions, classes). */
    symbols: z.array(SymbolRefSchema),
    /** Contracts touched, where cheap to detect (routes, migration table names, bindings). */
    contracts: z.array(ContractRefSchema),
  })
  .refine((f) => f.change !== "renamed" || f.previousPath !== undefined, {
    message: "renamed files need previousPath",
    path: ["previousPath"],
  });
/** See {@link DiffFileSchema}. */
export type DiffFile = z.infer<typeof DiffFileSchema>;

/**
 * What a session's push actually changed, relative to the base it forked from.
 * Produced by the diff workflow for each push and sent to the coordinator, which compares it
 * against other sessions' claims and diffs to raise `diff` overlaps.
 */
export const DiffSummarySchema = z.object({
  /** The session whose fork was pushed. */
  sessionId: IdSchema,
  /** The pushed commit. */
  commit: CommitShaSchema,
  /** The commit the diff is taken against (usually the fork point on main). */
  baseCommit: CommitShaSchema,
  /** Every changed file. */
  files: z.array(DiffFileSchema),
  /** When the summary was produced. */
  createdAt: TimestampSchema,
});
/** See {@link DiffSummarySchema}. */
export type DiffSummary = z.infer<typeof DiffSummarySchema>;
